import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { QuerySpec, QueryResult } from '@/types/query';
import { hashSpec } from '@/engine/spec-hash';
import {
  getQueryWorker,
  getWorkerGeneration,
  subscribeToWorkerGeneration,
} from '@/engine/worker-client';
import { useModeStore } from '@/store/mode-store';
import { useCompareConfigStore } from '@/store/compare-config-store';
import { formatPeriod } from '@/features/compare/format-period';

type UseQueryResultReturn = {
  data: QueryResult | null;
  isLoading: boolean;
  error: string | null;
  /**
   * Re-fetch the current spec, bypassing both the worker LRU and the
   * widget-local cache. Wired to the per-widget reload button (Phase
   * 10 step 3) for the "bazen grafikler eksik yükleniyor" UX nudge.
   */
  reload: () => void;
};

/**
 * Subscribe a widget to a QuerySpec. Re-executes when the spec changes
 * (compared by hash). Debounces rapid changes (e.g. filter typing).
 *
 * Compare-mode dispatch (D-020): when the active mode is `'compare'`
 * and a CompareConfig has been saved for the dataset, this hook
 * routes through `executeCompareQuery` instead of `executeQuery`.
 * Result rows then carry a `__period__` field and `compareMeta` is
 * populated; widgets pivot the rows for two-series rendering.
 *
 * Compare mode without a saved config returns `data=null` so widgets
 * render their loading / empty state until the user clicks
 * "Karşılaştır" in the config panel.
 */
export function useQueryResult(
  spec: QuerySpec | null,
  debounceMs = 150,
): UseQueryResultReturn {
  const { t } = useTranslation();
  const [data, setData] = useState<QueryResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the latest spec hash to ignore stale responses, and the worker
  // generation that produced our cached `data` so we know when a respawn
  // has invalidated it.
  const latestHash = useRef<string | null>(null);
  const dataGeneration = useRef<number>(-1);

  // Re-execute when the worker is respawned after a crash. The cache lives
  // in the worker so a generation bump means our last result is stale.
  const generation = useSyncExternalStore(
    subscribeToWorkerGeneration,
    getWorkerGeneration,
    getWorkerGeneration,
  );

  // Reload counter — bumped by the public `reload()` callback to force
  // a re-fetch even when the spec hash and worker generation are stable.
  // Threading it into `dispatchKey` makes the cached-result short-circuit
  // miss exactly once per reload click; subsequent renders settle.
  const [reloadKey, setReloadKey] = useState(0);

  const mode = useModeStore((s) => s.mode);
  const compareConfig = useCompareConfigStore((s) => s.config);
  const useCompare = mode === 'compare' && compareConfig !== null;
  const compareKey = useCompare
    ? `${compareConfig.dateColumn}|${compareConfig.granularity}|${compareConfig.periodA.year}-${compareConfig.periodA.sub ?? 0}|${compareConfig.periodB.year}-${compareConfig.periodB.sub ?? 0}`
    : null;
  const specKey = spec ? hashSpec(spec) : null;

  useEffect(() => {
    // Both bail-out branches must invalidate latestHash so any in-flight
    // promise can't sneak a stale result in after the spec or compare
    // config drops out from under us. Direct setters (not functional
    // updaters) — React 18 bails on Object.is equality, and StrictMode
    // double-invocation of functional updaters caused observed regressions.
    if (!spec) {
      latestHash.current = null;
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (useCompare === false && mode === 'compare') {
      // User is in compare mode but hasn't picked periods yet — no
      // sensible query to run. Show empty state via data=null.
      latestHash.current = null;
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const currentHash = hashSpec(spec);
    const dispatchKey = useCompare
      ? `compare:${currentHash}|${compareKey}|r${reloadKey}`
      : `${currentHash}|r${reloadKey}`;
    latestHash.current = dispatchKey;

    // Don't re-fetch if we already have this result from the live worker.
    if (
      data?.specHash === dispatchKey &&
      dataGeneration.current === generation
    )
      return;

    setIsLoading(true);
    setError(null);

    const timer = setTimeout(() => {
      const worker = getQueryWorker();
      const promise: Promise<QueryResult> = useCompare
        ? worker.executeCompareQuery(spec, compareConfig, {
            labelA: formatPeriod(t, compareConfig.granularity, compareConfig.periodA),
            labelB: formatPeriod(t, compareConfig.granularity, compareConfig.periodB),
          })
        : worker.executeQuery(spec);

      promise
        .then((result) => {
          if (latestHash.current === dispatchKey) {
            dataGeneration.current = generation;
            // Pin the dispatchKey on the cached result so the early-return
            // above stays consistent across mode flips on the same spec.
            setData({ ...result, specHash: dispatchKey });
            setIsLoading(false);
          }
        })
        .catch((err: unknown) => {
          if (latestHash.current === dispatchKey) {
            setError(err instanceof Error ? err.message : String(err));
            setIsLoading(false);
          }
        });
    }, debounceMs);

    return () => clearTimeout(timer);
    // `mode` must be in the deps even though it's only read inside the
    // bail check: transitioning from compare-without-config (useCompare
    // false, mode 'compare', bail taken) to normal mode flips `mode`
    // alone — useCompare and compareKey both stay false/null, so without
    // listing mode here the effect never re-runs and `data` stays
    // pinned to null. Symptom: the data preview table stays empty after
    // toggling out of an unconfigured compare-mode session.
    //
    // The hook also reads `spec`, `compareConfig`, `data?.specHash`, and
    // `t` inside the effect without listing them; each is intentional
    // — `specKey` already captures every observable change to `spec`,
    // `compareKey` does the same for `compareConfig`, the `data` read
    // is the early-return short-circuit (a re-trigger here would loop),
    // and `t` is the stable i18next function. Plan to remove on the
    // React 19 upgrade: switch these spec / compareConfig / t / data
    // reads to `useEffectEvent` so the deps array is fully exhaustive
    // and the disable can go. Covered by `useQueryResult.test.tsx`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey, debounceMs, generation, useCompare, compareKey, reloadKey, mode]);

  const reload = useCallback(() => {
    if (spec) {
      // Drop the worker's cached result for this dataset so the next
      // fetch hits DuckDB fresh — the user's reload click is meant to
      // recover from "bazen grafikler eksik yükleniyor", which is
      // typically a stale cache hit, not a connectivity issue.
      getQueryWorker()
        .invalidateCache(spec.source)
        .catch(() => {
          // Worker may not have spawned yet — nothing to invalidate.
        });
    }
    setReloadKey((k) => k + 1);
    // `spec.source` is read but not listed: `specKey` already hashes
    // the entire spec, so any source change invalidates the callback
    // identity. Plan to remove on the React 19 upgrade: wrap the
    // `spec.source` read in `useEffectEvent` so the dep can be added.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey]);

  return { data, isLoading, error, reload };
}
